import { cn } from '@/lib/infra/utils';

/**
 * SharpStar — the sharp-anchor cue (HYDRA_API "Unabated consensus update",
 * behavior C), replacing the earlier dotted amber underline.
 *
 * A superscript gold star (exponent position) shown only while the consensus
 * is Pinnacle-anchored (`sharpBookUsed === true`); false/unknown retain an
 * invisible equal-width spacer so a live anchor transition cannot reflow the
 * surrounding table. Field-only is the normal state, not an error. Freshness
 * reads from tone, same technique as TrendArrow: a grey base glyph with the
 * gold overlay at `recency` opacity, so the star burns bright on a fresh
 * frame and decays to nearly grey ~10s after the last update
 * (recency = exp(−age/3.3s) ≈ 0.05 at 10s), then rests grey while the
 * anchor holds.
 *
 * Size via `className` (e.g. "text-[10px]") — callers pick roughly 0.55×
 * their number's font size.
 */
export function SharpStar({
  sharp,
  recency,
  className,
}: {
  /** sharpBookUsed for the bucket; only `true` renders. */
  sharp: boolean | null | undefined;
  /** 0..1 frame recency for the bucket — the gold tone's share. */
  recency: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative -top-[0.4em] inline-block leading-none',
        sharp !== true && 'invisible',
        className,
      )}
      title={sharp === true ? 'Pinnacle-anchored (sharpBookUsed)' : undefined}
    >
      <span className="text-muted-foreground/60">★</span>
      <span
        className="absolute inset-0 text-amber-400 transition-opacity duration-200 ease-linear"
        style={{ opacity: Math.max(0, Math.min(1, recency)) }}
      >
        ★
      </span>
    </span>
  );
}
